// This reducer is intended for state that is used in multiple places throughout the app and doesn't pertain to one single reducer
import { nestedAction } from '../utils/Nested';
import { formatPrice } from '../utils/helpers';

const initialState = {
  clientConfig: {
    payerNameRequired: null,
    creditCardReaderType: null,
  },
  isClientConfigError: false,
  denominations: [],
  isLoading: false,
  isDenominationsError: false,
  isPinPadResetError: false,
  alerts: [],
  currentAbilities: {},
  tableFilterOptions: {
    error: false,
    isLoading: false,
    agencies: [],
    batches: [],
    locations: [],
    order_statuses: [],
    payment_methods: [],
    terminals: [],
    users: [],
  },
};

// For database driven configurations that vary by client
export const FETCH_CLIENT_CONFIG = 'FETCH_CLIENT_CONFIG';
export const fetchClientConfigRequest = (data) => ({
  type: FETCH_CLIENT_CONFIG,
  payload: data,
});
const CLIENT_CONFIG_SUCCESS = 'CLIENT_CONFIG_SUCCESS';
export const fetchClientConfigSuccess = (data) => ({
  type: CLIENT_CONFIG_SUCCESS,
  payload: data,
});
const CLIENT_CONFIG_ERROR = 'CLIENT_CONFIG_ERROR';
export const fetchClientConfigError = (data) => ({
  type: CLIENT_CONFIG_ERROR,
  payload: data,
});

// Permissions for currently logged in cashier
export const FETCH_CURRENT_ABILITIES = 'FETCH_CURRENT_ABILITIES';
export const fetchCurrentAbilitiesRequest = () => ({
  type: FETCH_CURRENT_ABILITIES,
});
const CURRENT_ABILITIES_SUCCESS = 'CURRENT_ABILITIES_SUCCESS';
export const fetchCurrentAbilitiesSuccess = (data) => ({
  type: CURRENT_ABILITIES_SUCCESS,
  payload: data,
});
const CURRENT_ABILITIES_ERROR = 'CURRENT_ABILITIES_ERROR';
export const fetchCurrentAbilitiesError = (data) => ({
  type: CURRENT_ABILITIES_ERROR,
  payload: data,
});

// Shared Alerts and Errors (the rest of the app's alerts are tied to pages/routes and can't be used on multiple)
export const ALERT_BAR_ACTION = 'shared/ALERT_BAR_ACTION';
export const sharedAlertBarAction = nestedAction(ALERT_BAR_ACTION);

const ADD_SHARED_ALERT = 'ADD_SHARED_ALERT';
export const addSharedAlert = (alert) => ({
  type: ADD_SHARED_ALERT,
  payload: alert,
});

export const genericErrorMessage = (err, heading = 'Error') => ({
  heading: `${heading}:`,
  text: err.message,
  variant: 'error',
});

const DELETE_SHARED_ALERT = 'DELETE_SHARED_ALERT';
export const deleteSharedAlert = (index) => ({
  type: DELETE_SHARED_ALERT,
  payload: { index },
});

const CLEAR_SHARED_ALERTS = 'CLEAR_SHARED_ALERTS';
export const clearSharedAlerts = () => ({ type: CLEAR_SHARED_ALERTS });

export const REPLACE_SHARED_ALERTS = 'REPLACE_SHARED_ALERTS';
export const replaceSharedAlerts = (alert) => ({
  type: REPLACE_SHARED_ALERTS,
  payload: { alert },
});

// Denominations Actions
export const FETCH_DENOMINATIONS = 'FETCH_DENOMINATIONS';
export const fetchDenominationsRequest = () => {
  return {
    type: FETCH_DENOMINATIONS,
  };
};

export const FETCH_DENOMINATIONS_SUCCESS = 'FETCH_DENOMINATIONS_SUCCESS';
export const fetchDenominationsSuccess = (data) => {
  return {
    type: FETCH_DENOMINATIONS_SUCCESS,
    payload: data,
  };
};

export const FETCH_DENOMINATIONS_ERROR = 'FETCH_DENOMINATIONS_ERROR';
export const fetchDenominationsError = (data) => ({
  type: FETCH_DENOMINATIONS_ERROR,
  payload: data,
});

// Table Filter Options actions
export const FETCH_ORDERS_FILTER_OPTIONS = 'FETCH_ORDERS_FILTER_OPTIONS';
export const fetchOrdersFilterOptionsRequest = () => ({
  type: FETCH_ORDERS_FILTER_OPTIONS,
});

const ORDERS_FILTER_OPTIONS_SUCCESS = 'ORDERS_FILTER_OPTIONS_SUCCESS';
export const fetchOrdersFilterOptionsSuccess = (data) => ({
  type: ORDERS_FILTER_OPTIONS_SUCCESS,
  payload: data,
});

const ORDERS_FILTER_OPTIONS_ERROR = 'ORDERS_FILTER_OPTIONS_ERROR';
export const fetchOrdersFilterOptionsError = (data) => ({
  type: ORDERS_FILTER_OPTIONS_ERROR,
  payload: data,
});

export const FETCH_DRAWER_HISTORIES_FILTER_OPTIONS =
  'FETCH_DRAWER_HISTORIES_FILTER_OPTIONS';
export const fetchDrawerHistoriesFilterOptionsRequest = () => ({
  type: FETCH_DRAWER_HISTORIES_FILTER_OPTIONS,
});

const DRAWER_HISTORIES_FILTER_OPTIONS_SUCCESS =
  'DRAWER_HISTORIES_FILTER_OPTIONS_SUCCESS';
export const fetchDrawerHistoriesFilterOptionsSuccess = (data) => ({
  type: DRAWER_HISTORIES_FILTER_OPTIONS_SUCCESS,
  payload: data,
});

const DRAWER_HISTORIES_FILTER_OPTIONS_ERROR =
  'DRAWER_HISTORIES_FILTER_OPTIONS_ERROR';
export const fetchDrawerHistoriesFilterOptionsError = (data) => ({
  type: DRAWER_HISTORIES_FILTER_OPTIONS_ERROR,
  payload: data,
});

export const RESET_PIN_PAD = 'RESET_PIN_PAD';
export const resetPinPadRequest = (terminalId) => ({
  type: RESET_PIN_PAD,
  payload: {
    terminalId,
  },
});

const RESET_PIN_PAD_SUCCESS = 'RESET_PIN_PAD_SUCCESS';
export const resetPinPadSuccess = (data) => ({
  type: RESET_PIN_PAD_SUCCESS,
  payload: data,
});

const RESET_PIN_PAD_ERROR = 'RESET_PIN_PAD_ERROR';
export const resetPinPadError = (data) => ({
  type: RESET_PIN_PAD_ERROR,
  payload: data,
});

export const reducer = (state = initialState, { type, payload } = action) => {
  switch (type) {
    case RESET_PIN_PAD: {
      return {
        ...state,
        isLoading: true,
      };
    }
    case RESET_PIN_PAD_SUCCESS: {
      return {
        ...state,
        isPinPadResetError: false,
        isLoading: false,
      };
    }
    case RESET_PIN_PAD_ERROR: {
      return {
        ...state,
        isPinPadResetError: true,
        isLoading: false,
      };
    }
    case FETCH_ORDERS_FILTER_OPTIONS:
      return {
        ...state,
        tableFilterOptions: {
          ...state.tableFilterOptions,
          isLoading: true,
        },
      };
    case ORDERS_FILTER_OPTIONS_SUCCESS:
      return {
        ...state,
        tableFilterOptions: {
          ...state.tableFilterOptions,
          ...payload.data,
          isLoading: false,
          error: false,
        },
      };
    case ORDERS_FILTER_OPTIONS_ERROR:
      return {
        ...state,
        tableFilterOptions: {
          ...state.tableFilterOptions,
          isLoading: false,
          error: true,
        },
      };
    case FETCH_DRAWER_HISTORIES_FILTER_OPTIONS:
      return {
        ...state,
        tableFilterOptions: {
          ...state.tableFilterOptions,
          isLoading: true,
        },
      };
    case DRAWER_HISTORIES_FILTER_OPTIONS_SUCCESS:
      return {
        ...state,
        tableFilterOptions: {
          ...state.tableFilterOptions,
          ...payload.data,
          isLoading: false,
          error: false,
        },
      };
    case DRAWER_HISTORIES_FILTER_OPTIONS_ERROR:
      return {
        ...state,
        tableFilterOptions: {
          ...state.tableFilterOptions,
          isLoading: false,
          error: true,
        },
      };
    case FETCH_CLIENT_CONFIG:
      return {
        ...state,
        isLoading: true,
      };
    case CLIENT_CONFIG_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isClientConfigError: false,
        clientConfig: {
          ...state.clientConfig,
          payerNameRequired: payload.payer_name_required,
          creditCardReaderType: payload.credit_card_reader_type,
        },
      };
    case CLIENT_CONFIG_ERROR:
      return {
        ...state,
        isLoading: false,
        isClientConfigError: true,
      };
    case FETCH_CURRENT_ABILITIES:
      return {
        ...state,
        isLoading: true,
      };
    case CURRENT_ABILITIES_SUCCESS:
      return {
        ...state,
        isLoading: false,
        currentAbilities: payload,
      };
    case CURRENT_ABILITIES_ERROR:
      return {
        ...state,
        isLoading: false,
      };
    case FETCH_DENOMINATIONS:
      return {
        ...state,
        isLoading: true,
      };
    case FETCH_DENOMINATIONS_ERROR:
      return {
        ...state,
        isDenominationsError: true,
        isLoading: false,
      };
    case FETCH_DENOMINATIONS_SUCCESS:
      return {
        ...state,
        denominations: payload.denominations
          .map((d) => ({ ...d, formattedValue: formatPrice(d.value) }))
          .sort((a, b) => a.value - b.value),
        isDenominationsSuccess: true,
        isLoading: false,
        isDenominationsError: false,
      };
    case ADD_SHARED_ALERT:
      return { ...state, alerts: [...state.alerts, payload.alert] };
    case DELETE_SHARED_ALERT:
      return {
        ...state,
        alerts: state.alerts.filter(
          (a) => state.alerts.indexOf(a) !== payload.index,
        ),
      };
    case CLEAR_SHARED_ALERTS:
      return { ...state, alerts: initialState.alerts };
    case REPLACE_SHARED_ALERTS:
      return { ...state, alerts: [payload.alert] };
    default:
      return state;
  }
};

export default reducer;
